import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpErrorResponse, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, EMPTY, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '@env/environment';
import { Logger } from '../logger.service';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to all requests.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private router: Router | null = null;

  constructor(
    private _snackBar: MatSnackBar,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //console.log('ErrorHandlerInterceptor');
    return next.handle(request).pipe(catchError((error) => this.handleError(error, request)));
  }

  private handleError(error: HttpErrorResponse, request?: HttpRequest<any>) {
    // Skip error handling for successful responses (200-299)
    // Sometimes Angular's HttpClient can create HttpErrorResponse objects even for successful responses
    // in edge cases (e.g., malformed JSON, CORS issues, etc.), but we should only handle actual errors
    if (error.status >= 200 && error.status < 300) {
      // This is actually a successful response, don't log it as an error
      // Just re-throw it so the calling code can handle it (they should check error.error for the response body)
      return throwError(error);
    }

    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
      console.error('Full error object:', error);
      console.error('Error headers:', error.headers);
      console.error('Error URL:', error.url);

      // Handle 401 Unauthorized - redirect to login if not already there
      if (error.status === 401) {
        const isRefreshEndpoint = request?.url?.includes('/auth/refresh');

        if (isRefreshEndpoint) {
          return throwError(() => error);
        }

        if (!this.router) {
          this.router = this.injector.get(Router);
        }

        if (this.router && !this.router.url.includes('/login')) {
          console.log('ErrorHandlerInterceptor: Redirecting to login due to 401 error');
          this.router.navigate(['/login'], {
            queryParams: {
              redirect: this.router.url
            },
            replaceUrl: false
          });
        }

        this._snackBar.open(
          'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
          'Schließen',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 5000,
          }
        );
      } else {
        // error.status == 504
        if (error.status < 200 || error.status > 350) {
          if (error.error && error.error.error == true) {
            // custom error message
            this._snackBar.open('' + error.error.message, 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          } else if (error.error && error.error.error) {
            // Handle error object with error property
            this._snackBar.open('' + error.error.error, 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          } else {
            /*|| error.status != 'undefined' */
            this._snackBar.open(
              'Die Internet-Verbindung ist nicht stabil. Sie können weiterarbeiten, doch kann es zu Verzögerungen kommen.',
              'Schließen',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 3000,
              }
            );
          }
        }
      }
    }
    // Return an observable with a user-facing error message.
    // For debugging, pass through the original error to see backend details
    return throwError(error);
  }

  /*
  // Customize the default error handler here if needed
  private errorHandler(error: HttpEvent<any>): Observable<HttpEvent<any>> {

    if (error instanceof Error) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(`Backend returned code ${error}, body was: ${error}`);
    }

    // If you want to return a new response:
    //return of(new HttpResponse({body: [{name: "Default value..."}]}));

    // If you want to return the error on the upper level:
    //return throwError(error);

    // or just return nothing:
    return EMPTY;

    if (!environment.production) {
      // Do something with the error
      log.error('Request error', error);
    }
    throw error;
  }
  */
}
